import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Paper,
    Grid,
    Typography,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    IconButton,
    Tooltip,
    CircularProgress,
    Box,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    LinearProgress,
    FormControlLabel,
    Checkbox
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@mui/material";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import PersonIcon from '@material-ui/icons/Person';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import AssignmentIcon from '@material-ui/icons/Assignment';
import WorkIcon from '@material-ui/icons/Work';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import PrintIcon from '@material-ui/icons/Print';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import UserService from "../../services/UserService";
import MainContainer from "../../components/MainContainer";
import { headerContract } from "../../data/modules/contract_header";
import ContractService from "../../services/ContractService";
import logo from '../../assets/images/logo.png';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(2),
    },
    header: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#1976d2',
        borderRadius: '8px',
        color: 'white',
    },
    backButton: {
        marginBottom: theme.spacing(2),
    },
    sectionCard: {
        marginBottom: theme.spacing(3),
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
    infoRow: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
        borderBottom: '1px solid #e0e0e0',
    },
    infoLabel: {
        fontWeight: 'bold',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    infoValue: {
        fontSize: '1.1rem',
    },
    documentCard: {
        textAlign: 'center',
        padding: theme.spacing(3),
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
        },
    },
    documentIcon: {
        fontSize: 60,
        marginBottom: theme.spacing(1),
    },
    documentTitle: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
    },
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1.5),
        borderRadius: '20px',
        fontSize: '0.875rem',
        fontWeight: 'bold',
    },
    statusComplete: {
        backgroundColor: '#4caf50',
        color: 'white',
    },
    statusMissing: {
        backgroundColor: '#f44336',
        color: 'white',
    },
    statusPartial: {
        backgroundColor: '#ff9800',
        color: 'white',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
    },
    fileUpload: {
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: theme.spacing(3),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: '#f5f5f5',
        },
    },
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
        marginTop: theme.spacing(1),
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
    },
    attestationPreview: {
        backgroundColor: '#f5f5f5',
        padding: theme.spacing(3),
        borderRadius: theme.spacing(1),
        marginTop: theme.spacing(2),
        maxHeight: '700px',
        overflow: 'auto',
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationContent: {
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto',
        position: 'relative',
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '10px',
        flexShrink: 0,
    },
    logoLeft: {
        width: '250px',
        height: 'auto',
        objectFit: 'contain',
    },
    attestationDivider: {
        border: 'none',
        borderTop: '2px solid #333',
        margin: '10px 0 20px 0',
        flexShrink: 0,
    },
    attestationRef: {
        textAlign: 'left',
        marginBottom: '20px',
        fontSize: '14px',
        marginTop: '10px',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationTitle: {
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
        marginBottom: '20px',
        textDecoration: 'underline',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationBody: {
        lineHeight: '1.6',
        fontSize: '12px',
        textAlign: 'justify',
        flex: '1 1 auto',
        marginBottom: '20px',
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationSignature: {
        marginTop: '40px',
        textAlign: 'right',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationDate: {
        marginTop: '20px',
        textAlign: 'right',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    attestationFooter: {
        marginTop: '30px',
        textAlign: 'center',
        fontSize: '10px',
        color: '#4874d1',
        borderTop: '1px solid #ddd',
        paddingTop: '10px',
        fontStyle: 'italic',
        fontWeight: 'bold',
        flexShrink: 0,
        fontFamily: 'Times New Roman, Times, serif',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
    },
    dialogContent: {
        overflowX: 'hidden',
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'flex-end',
    },
}));

export default function UserDetails() {
    const classes = useStyles();
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [personnel, setPersonnel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // États pour les dialogues
    const [contractDialogOpen, setContractDialogOpen] = useState(false);
    const [assuranceDialogOpen, setAssuranceDialogOpen] = useState(false);
    const [attestationDialogOpen, setAttestationDialogOpen] = useState(false);
    
    // États pour les fichiers
    const [contractFile, setContractFile] = useState(null);
    const [assuranceFile, setAssuranceFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    
    // États pour l'attestation de travail
    const [showAttestationPreview, setShowAttestationPreview] = useState(false);
    const [generatingAttestation, setGeneratingAttestation] = useState(false);
    const previewRef = useRef(null);
    
    const [attestationData, setAttestationData] = useState({
        nomPrenom: '',
        dateEmbauche: '',
        dateFin: '',
        estToujoursEnPoste: false,
        fonction: '',
        service: '',
        direction: '',
        directeurNom: 'RAZAKATOANINA Achille',
        organisme: "FONDS D'INTERVENTION POUR LE DEVELOPPEMENT (FID)",
        reference: '',
        lieu: 'Antananarivo',
        dateDelivrance: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        loadPersonnelDetails();
    }, [id]);

    useEffect(() => {
        if (personnel && attestationDialogOpen) {
            setAttestationData(prev => ({
                ...prev,
                nomPrenom: personnel.full_name || `${personnel.last_name || ''} ${personnel.first_name || ''}`.trim(),
                fonction: personnel.function_name || '',
                service: personnel.service_name || '',
                direction: personnel.direction_name || 'Direction Principale chargée de la Coordination',
            }));
        }
    }, [personnel, attestationDialogOpen]);

    const loadPersonnelDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await ContractService.detailuser(id);
            if (result && result.data) {
                setPersonnel(result.data.data);
            } else {
                setError("Erreur lors du chargement des données");
            }
        } catch (err) {
            console.error("Erreur:", err);
            setError("Impossible de charger les détails du personnel");
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (type) => (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'application/pdf') {
            if (type === 'contrat') {
                setContractFile(file);
            } else if (type === 'assurance') {
                setAssuranceFile(file);
            }
            setError(null);
        } else {
            setError("Veuillez sélectionner un fichier PDF valide");
        }
    };

    const handleUploadContract = async () => {
        if (!contractFile) {
            setError("Veuillez sélectionner un fichier");
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('personnel_id', personnel.user_id || personnel.person_id);
            formData.append('document_type', 'contrat');
            formData.append('file', contractFile);
            
            const response = await UserService.uploadPersonnelDocument(formData);
            if (response && response.status) {
                setContractDialogOpen(false);
                setContractFile(null);
                await loadPersonnelDetails();
            }
        } catch (err) {
            setError("Erreur lors de l'upload du contrat");
        } finally {
            setUploading(false);
        }
    };

    const handleUploadAssurance = async () => {
        if (!assuranceFile) {
            setError("Veuillez sélectionner un fichier");
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('personnel_id', personnel.user_id || personnel.person_id);
            formData.append('document_type', 'assurance');
            formData.append('file', assuranceFile);
            
            const response = await UserService.uploadPersonnelDocument(formData);
            if (response && response.status) {
                setAssuranceDialogOpen(false);
                setAssuranceFile(null);
                await loadPersonnelDetails();
            }
        } catch (err) {
            setError("Erreur lors de l'upload de l'attestation");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDocument = async (type) => {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer ce document ?`)) {
            setUploading(true);
            try {
                const response = await UserService.deletePersonnelDocument(
                    personnel.user_id || personnel.person_id, 
                    type
                );
                if (response && response.status) {
                    await loadPersonnelDetails();
                }
            } catch (err) {
                setError("Erreur lors de la suppression");
            } finally {
                setUploading(false);
            }
        }
    };

    // Visualisation directe du document (sans code PIN)
    const handleViewDocument = async (documentType) => {
        setLoading(true);
        try {
            const result = await ContractService.viewDocument(
                personnel.user_id || personnel.person_id, 
                documentType
            );
            if (result && result.status) {
                // Ouvrir le document dans un nouvel onglet
                const pdfWindow = window.open();
                pdfWindow.document.write(`
                    <html>
                        <head>
                            <title>${documentType === 'contrat' ? 'Contrat de travail' : 'Attestation d\'assurance'}</title>
                            <style>
                                body { margin: 0; padding: 0; }
                                embed { width: 100%; height: 100vh; }
                            </style>
                        </head>
                        <body>
                            <embed src="data:${result.data.file_type};base64,${result.data.file_content}" width="100%" height="100%" />
                        </body>
                    </html>
                `);
                pdfWindow.document.close();
            } else {
                setError(result.message || "Erreur lors de l'ouverture du document");
            }
        } catch (err) {
            console.error('Erreur:', err);
            setError("Erreur lors de l'ouverture du document");
        } finally {
            setLoading(false);
        }
    };

    // Génération de référence automatique
    const generateReference = useCallback(() => {
        const now = new Date();
        const year = now.getFullYear().toString().slice(-2);
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const random = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
        return `ATT/${random}/${day}/${month}/${year}`;
    }, []);

    // Formatage des dates
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return '';
        }
    }, []);

    // Gestionnaire de changement
    const handleAttestationChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setAttestationData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    // Contenu HTML de l'attestation de travail
    const attestationHTML = useMemo(() => {
        const refValue = attestationData.reference || generateReference();
        const nomComplet = attestationData.nomPrenom || '_______________';
        const periodeTexte = attestationData.estToujoursEnPoste
            ? `depuis le ${formatDate(attestationData.dateEmbauche)} jusqu'à ce jour`
            : attestationData.dateFin
                ? `du ${formatDate(attestationData.dateEmbauche)} au ${formatDate(attestationData.dateFin)}`
                : `depuis le ${formatDate(attestationData.dateEmbauche)}`;
        
        return (
            <div 
                id="attestation-pdf" 
                className={classes.attestationContent}
                ref={previewRef}
                style={{
                    minHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    fontFamily: 'Times New Roman, Times, serif'
                }}
            >
                <div className={classes.attestationHeader}>
                    <img 
                        src={logo} 
                        alt="Logo" 
                        className={classes.logoLeft}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className={classes.headerCenter}></div>
                </div>
                
                <hr className={classes.attestationDivider} />
                
                <div className={classes.attestationRef}>
                    <strong>N/Réf :</strong> {refValue}
                </div>
                
                <div className={classes.attestationTitle}>
                    <strong style={{ fontStyle: 'italic', fontSize: '30px', fontFamily: 'Times New Roman, Times, serif' }}>
                        ATTESTATION DE TRAVAIL
                    </strong>
                </div>
                
                <div className={classes.attestationBody} style={{ fontFamily: 'Times New Roman, Times, serif' }}>
                    <p style={{ marginBottom: 15, fontFamily: 'Times New Roman, Times, serif', color: 'black', fontSize: '16px' }}>
                        Nous soussignés, <strong>{attestationData.organisme}</strong>,
                        représenté par son Directeur Général <strong>{attestationData.directeurNom}</strong>, attestons par la présente que :
                    </p>
                    <p style={{ marginBottom: 15, textIndent: 30, fontFamily: 'Times New Roman, Times, serif', color: 'black', fontSize: '16px' }}>
                        <strong>Monsieur/Madame {nomComplet}</strong> est/s'était employé(e) au sein de notre institution en tant que{' '}
                        <strong>{attestationData.fonction || '_______________'}</strong> au service{' '}
                        <strong>{attestationData.service || '_______________'}</strong> de la{' '}
                        <strong>{attestationData.direction || '_______________'}</strong> {periodeTexte}.
                    </p>
                    {!attestationData.estToujoursEnPoste && attestationData.dateFin && (
                        <p style={{ marginBottom: 15, fontFamily: 'Times New Roman, Times, serif', color: 'black', fontSize: '16px' }}>
                            L'intéressé(e) a cessé ses fonctions à compter du <strong>{formatDate(attestationData.dateFin)}</strong>.
                        </p>
                    )}
                    <p style={{ marginTop: 20, marginBottom: 15, fontFamily: 'Times New Roman, Times, serif', color: 'black', fontSize: '16px' }}>
                        L'intéressé(e) a fait preuve de professionnalisme, de sérieux et de dévouement dans l'exercice de ses fonctions.
                        Il/Elle a rempli ses missions avec rigueur et efficacité, contribuant ainsi à l'atteinte des objectifs de notre institution.
                    </p>
                    <p style={{ marginTop: 20, fontFamily: 'Times New Roman, Times, serif', color: 'black', fontSize: '16px' }}>
                        En foi de quoi, la présente attestation lui est délivrée pour servir ce que de droit.
                    </p>
                </div>

                <div className={classes.attestationDate} style={{ fontFamily: 'Times New Roman, Times, serif', color: 'black', fontSize: '16px' }}>
                    Fait à {attestationData.lieu}, le {formatDate(attestationData.dateDelivrance)}
                </div>
                
                <div className={classes.attestationSignature} style={{ fontFamily: 'Times New Roman, Times, serif', color: 'black', fontSize: '16px' }}>
                    Le Directeur Général,
                    <br /><br /><br />
                    <strong>{attestationData.directeurNom}</strong>
                </div>
                
                <div className={classes.attestationFooter} style={{ fontFamily: 'Times New Roman, Times, serif' }}>
                    <p>"Fenitra tratrarina mampivoatra sy manarina"</p>
                </div>
            </div>
        );
    }, [attestationData, classes, generateReference, formatDate]);

    // Génération PDF de l'attestation
    const generateAttestationPDF = useCallback(async () => {
        if (!attestationData.nomPrenom.trim()) {
            setError('Veuillez saisir le nom et prénom');
            return;
        }

        if (!attestationData.dateEmbauche) {
            setError('Veuillez saisir la date d\'embauche');
            return;
        }

        setGeneratingAttestation(true);
        setError('');
        
        try {
            const element = document.getElementById('attestation-pdf');
            if (!element) throw new Error("Élément non trouvé");
            
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.top = '-9999px';
            tempContainer.style.left = '-9999px';
            tempContainer.style.width = '800px';
            tempContainer.style.backgroundColor = 'white';
            tempContainer.style.fontFamily = 'Times New Roman, Times, serif';
            tempContainer.appendChild(element.cloneNode(true));
            document.body.appendChild(tempContainer);
            
            const canvas = await html2canvas(element, {
                scale: 3,
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true,
                allowTaint: false,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
                onclone: (clonedDoc, element) => {
                    const clonedElement = clonedDoc.getElementById('attestation-pdf');
                    if (clonedElement) {
                        clonedElement.style.fontFamily = 'Times New Roman, Times, serif';
                        const allElements = clonedElement.getElementsByTagName('*');
                        for (let i = 0; i < allElements.length; i++) {
                            allElements[i].style.fontFamily = 'Times New Roman, Times, serif';
                        }
                    }
                }
            });
            
            document.body.removeChild(tempContainer);
            
            const imgData = canvas.toDataURL('image/jpeg', 0.98);
            const pdf = new jsPDF({
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait',
                compress: true,
                hotfixes: ['px_scaling']
            });
            
            const pageWidth = 210;
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const xPosition = (pageWidth - imgWidth) / 2;
            let yPosition = 10;
            
            if (imgHeight < 277) {
                yPosition = (277 - imgHeight) / 2;
            }
            
            pdf.addImage(imgData, 'JPEG', xPosition, yPosition, imgWidth, imgHeight);
            
            const pdfBlob = pdf.output('blob');
            const uploadFormData = new FormData();
            const fileName = `attestation_travail_${attestationData.nomPrenom.replace(/\s/g, '_')}.pdf`;
            uploadFormData.append('file_attestation_travail', pdfBlob, fileName);
            uploadFormData.append('personnel_id', personnel?.user_id || personnel?.person_id);
            uploadFormData.append('personnel_nom', attestationData.nomPrenom);
            uploadFormData.append('date_embauche', attestationData.dateEmbauche);
            uploadFormData.append('date_fin', attestationData.dateFin || '');
            uploadFormData.append('est_toujours_en_poste', attestationData.estToujoursEnPoste);
            
            const result = await ContractService.uploadWorkAttestation(uploadFormData);
            
            if (result && result.status) {
                pdf.save(fileName);
                setAttestationDialogOpen(false);
                setShowAttestationPreview(false);
                await loadPersonnelDetails();
            } else {
                setError(result.message || 'Erreur lors de la sauvegarde');
            }
        } catch (err) {
            console.error('Erreur PDF:', err);
            setError('Erreur lors de la génération du PDF');
        } finally {
            setGeneratingAttestation(false);
        }
    }, [attestationData, personnel]);

    const handleSubmitAttestation = useCallback(() => {
        if (!attestationData.nomPrenom.trim()) {
            setError('Veuillez saisir le nom et prénom');
            return;
        }
        if (!attestationData.dateEmbauche) {
            setError('Veuillez saisir la date d\'embauche');
            return;
        }
        setError('');
        setShowAttestationPreview(true);
    }, [attestationData.nomPrenom, attestationData.dateEmbauche]);

    const handleCloseAttestation = useCallback(() => {
        setShowAttestationPreview(false);
        setError('');
        setAttestationDialogOpen(false);
    }, []);

    const getDocumentStatus = () => {
        if (!personnel) return { label: 'Incomplet', color: 'statusMissing', icon: <CancelIcon /> };
        
        const hasContrat = personnel.has_contract === 1 || personnel.contract_file;
        const hasAssurance = personnel.has_assurance === 1 || personnel.assurance_file;
        
        if (hasContrat && hasAssurance) {
            return { label: 'Dossier Complet', color: 'statusComplete', icon: <CheckCircleIcon /> };
        } else if (!hasContrat && !hasAssurance) {
            return { label: 'Dossier Manquant', color: 'statusMissing', icon: <CancelIcon /> };
        } else {
            return { label: 'Dossier Partiel', color: 'statusPartial', icon: <WarningIcon /> };
        }
    };

    if (loading) {
        return (
            <MainContainer {...headerContract}>
                <div className={classes.loadingContainer}>
                    <CircularProgress size={60} />
                </div>
            </MainContainer>
        );
    }

    if (error || !personnel) {
        return (
            <MainContainer {...headerContract}>
                <Container className={classes.container}>
                    <Alert severity="error" style={{ marginBottom: '20px' }}>
                        {error || "Personnel non trouvé"}
                    </Alert>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/contract_user')}
                    >
                        Retour à la liste
                    </Button>
                </Container>
            </MainContainer>
        );
    }

    const documentStatus = getDocumentStatus();
    const hasContrat = personnel.has_contract === 1 || personnel.contract_file;
    const hasAssurance = personnel.has_assurance === 1 || personnel.assurance_file;

    return (
        <MainContainer {...headerContract}>
            <Container maxWidth="lg" className={classes.container}>
                <Paper className={classes.header}>
                    <Breadcrumbs style={{ color: 'white', marginBottom: '16px' }}>
                        <Typography color="textPrimary">Détails du personnel</Typography>
                    </Breadcrumbs>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={2}>
                            <PersonIcon style={{ fontSize: 40 }} />
                            <div>
                                <Typography variant="h4">
                                    {personnel.full_name?.toUpperCase() || `${personnel.last_name || ''} ${personnel.first_name || ''}`.trim()}
                                </Typography>
                                <Typography variant="subtitle1">
                                    Matricule: {personnel.registration_number || 'Non renseigné'}
                                </Typography>
                            </div>
                        </Box>
                        <div className={`${classes.statusBadge} ${classes[documentStatus.color]}`}>
                            {documentStatus.icon} {documentStatus.label}
                        </div>
                    </Box>
                </Paper>

                <Button
                    className={classes.backButton}
                    variant="outlined"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/contract_user')}
                >
                    Retour à la liste du personnel
                </Button>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Informations personnelles"
                                avatar={<PersonIcon color="primary" />}
                            />
                            <Divider />
                            <CardContent>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>Matricule</Typography>
                                    <Typography className={classes.infoValue}>
                                        {personnel.registration_number || personnel.card_number || 'Non renseigné'}
                                    </Typography>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>
                                        <EmailIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                        Email personnel
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {personnel.email || 'Non renseigné'}
                                    </Typography>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>
                                        <PhoneIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                        Téléphone
                                    </Typography>
                                    <Typography className={classes.infoValue}>
                                        {personnel.telephone || 'Non renseigné'}
                                    </Typography>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Informations professionnelles"
                                avatar={<WorkIcon color="primary" />}
                                action={
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        startIcon={<PrintIcon />}
                                        onClick={() => setAttestationDialogOpen(true)}
                                    >
                                        Attestation de travail
                                    </Button>
                                }
                            />
                            <Divider />
                            <CardContent>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>Direction</Typography>
                                    <Typography className={classes.infoValue}>
                                        {personnel.direction_name || 'Non renseignée'}
                                    </Typography>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>Service</Typography>
                                    <Typography className={classes.infoValue}>
                                        {personnel.service_name || 'Non renseigné'}
                                    </Typography>
                                </div>
                                <div className={classes.infoRow}>
                                    <Typography className={classes.infoLabel}>Fonction</Typography>
                                    <Typography className={classes.infoValue}>
                                        {personnel.function_name || 'Non renseignée'}
                                    </Typography>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Contrat de travail"
                                avatar={<AssignmentIcon color="primary" />}
                                action={
                                    !hasContrat && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => setContractDialogOpen(true)}
                                        >
                                            Ajouter
                                        </Button>
                                    )
                                }
                            />
                            <Divider />
                            <CardContent>
                                {hasContrat ? (
                                    <Box>
                                        <Paper 
                                            className={classes.documentCard}
                                            onClick={() => handleViewDocument('contrat')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <PictureAsPdfIcon 
                                                className={classes.documentIcon}
                                                style={{ color: '#4caf50' }}
                                            />
                                            <Typography className={classes.documentTitle}>Contrat de travail</Typography>
                                            <Box display="flex" gap={1} justifyContent="center" mt={1}>
                                                <Tooltip title="Visualiser">
                                                    <Button size="small" color="primary" startIcon={<PictureAsPdfIcon />}>
                                                        Visualiser
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Supprimer">
                                                    <IconButton 
                                                        size="small" 
                                                        color="secondary" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteDocument('contrat');
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Paper>
                                    </Box>
                                ) : (
                                    <Box textAlign="center" py={3}>
                                        <Typography variant="body2" color="textSecondary">
                                            Aucun contrat de travail
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card className={classes.sectionCard}>
                            <CardHeader 
                                title="Assurance"
                                avatar={<DescriptionIcon color="primary" />}
                                action={
                                    !hasAssurance && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => setAssuranceDialogOpen(true)}
                                        >
                                            Ajouter
                                        </Button>
                                    )
                                }
                            />
                            <Divider />
                            <CardContent>
                                {hasAssurance ? (
                                    <Box>
                                        <Paper 
                                            className={classes.documentCard}
                                            onClick={() => handleViewDocument('assurance')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <PictureAsPdfIcon 
                                                className={classes.documentIcon}
                                                style={{ color: '#4caf50' }}
                                            />
                                            <Typography className={classes.documentTitle}>Attestation d'assurance</Typography>
                                            <Box display="flex" gap={1} justifyContent="center" mt={1}>
                                                <Tooltip title="Visualiser">
                                                    <Button size="small" color="primary" startIcon={<PictureAsPdfIcon />}>
                                                        Visualiser
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Supprimer">
                                                    <IconButton 
                                                        size="small" 
                                                        color="secondary" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteDocument('assurance');
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Paper>
                                    </Box>
                                ) : (
                                    <Box textAlign="center" py={3}>
                                        <Typography variant="body2" color="textSecondary">
                                            Aucune Assurance
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Dialogue upload contrat */}
                <Dialog open={contractDialogOpen} onClose={() => setContractDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Ajouter le contrat de travail</DialogTitle>
                    <DialogContent>
                        {error && <Alert severity="error" style={{ marginBottom: '16px' }}>{error}</Alert>}
                        {uploading && <LinearProgress style={{ marginBottom: '16px' }} />}
                        
                        <Box mt={2}>
                            <Typography variant="body2" gutterBottom>
                                Sélectionnez le fichier PDF du contrat
                            </Typography>
                            <div className={classes.fileUpload} onClick={() => document.getElementById('contract-upload').click()}>
                                <CloudUploadIcon style={{ fontSize: 48, color: '#ccc' }} />
                                <Typography variant="body2" color="textSecondary">
                                    Cliquez pour sélectionner le fichier PDF
                                </Typography>
                                <input
                                    id="contract-upload"
                                    type="file"
                                    accept="application/pdf"
                                    onChange={handleFileChange('contrat')}
                                    style={{ display: 'none' }}
                                />
                            </div>
                            {contractFile && (
                                <div className={classes.fileInfo}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <PictureAsPdfIcon style={{ color: '#f44336' }} />
                                        <Typography variant="body2">{contractFile.name}</Typography>
                                    </div>
                                    <IconButton size="small" onClick={() => setContractFile(null)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setContractDialogOpen(false)} disabled={uploading}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleUploadContract} 
                            color="primary" 
                            variant="contained" 
                            disabled={!contractFile || uploading}
                        >
                            {uploading ? <CircularProgress size={24} /> : 'Uploader'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialogue upload attestation assurance */}
                <Dialog open={assuranceDialogOpen} onClose={() => setAssuranceDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Ajouter l'attestation d'assurance</DialogTitle>
                    <DialogContent>
                        {error && <Alert severity="error" style={{ marginBottom: '16px' }}>{error}</Alert>}
                        {uploading && <LinearProgress style={{ marginBottom: '16px' }} />}
                        
                        <Box mt={2}>
                            <Typography variant="body2" gutterBottom>
                                Sélectionnez le fichier PDF de l'assurance
                            </Typography>
                            <div className={classes.fileUpload} onClick={() => document.getElementById('assurance-upload').click()}>
                                <CloudUploadIcon style={{ fontSize: 48, color: '#ccc' }} />
                                <Typography variant="body2" color="textSecondary">
                                    Cliquez pour sélectionner le fichier PDF
                                </Typography>
                                <input
                                    id="assurance-upload"
                                    type="file"
                                    accept="application/pdf"
                                    onChange={handleFileChange('assurance')}
                                    style={{ display: 'none' }}
                                />
                            </div>
                            {assuranceFile && (
                                <div className={classes.fileInfo}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <PictureAsPdfIcon style={{ color: '#f44336' }} />
                                        <Typography variant="body2">{assuranceFile.name}</Typography>
                                    </div>
                                    <IconButton size="small" onClick={() => setAssuranceFile(null)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAssuranceDialogOpen(false)} disabled={uploading}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleUploadAssurance} 
                            color="primary" 
                            variant="contained" 
                            disabled={!assuranceFile || uploading}
                        >
                            {uploading ? <CircularProgress size={24} /> : 'Uploader'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialogue Attestation de travail */}
                <Dialog 
                    open={attestationDialogOpen} 
                    onClose={handleCloseAttestation} 
                    maxWidth="md" 
                    fullWidth
                    disableBackdropClick={generatingAttestation}
                    disableEscapeKeyDown={generatingAttestation}
                >
                    <DialogTitle>
                        <Typography variant="h6">📄 Créer une attestation de travail</Typography>
                        <Typography variant="subtitle2" color="textSecondary">
                            Remplissez le formulaire pour générer l'attestation
                        </Typography>
                    </DialogTitle>
                    
                    <DialogContent className={classes.dialogContent}>
                        {error && <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError('')}>{error}</Alert>}
                        
                        {!showAttestationPreview ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Nom et prénom *"
                                        name="nomPrenom"
                                        value={attestationData.nomPrenom}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Date d'embauche *"
                                        name="dateEmbauche"
                                        type="date"
                                        value={attestationData.dateEmbauche}
                                        onChange={handleAttestationChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Date de fin (si cessation)"
                                        name="dateFin"
                                        type="date"
                                        value={attestationData.dateFin}
                                        onChange={handleAttestationChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation || attestationData.estToujoursEnPoste}
                                        helperText={attestationData.estToujoursEnPoste ? "Désactivez 'Toujours en poste' pour définir une date de fin" : ""}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={attestationData.estToujoursEnPoste}
                                                onChange={handleAttestationChange}
                                                name="estToujoursEnPoste"
                                                color="primary"
                                                disabled={generatingAttestation}
                                            />
                                        }
                                        label="Toujours en poste (jusqu'à ce jour)"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Fonction"
                                        name="fonction"
                                        value={attestationData.fonction}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Service"
                                        name="service"
                                        value={attestationData.service}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Direction"
                                        name="direction"
                                        value={attestationData.direction}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Lieu"
                                        name="lieu"
                                        value={attestationData.lieu}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="Date de délivrance"
                                        name="dateDelivrance"
                                        type="date"
                                        value={attestationData.dateDelivrance}
                                        onChange={handleAttestationChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Nom du Directeur Général"
                                        name="directeurNom"
                                        value={attestationData.directeurNom}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Organisme"
                                        name="organisme"
                                        value={attestationData.organisme}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Référence (optionnel)"
                                        name="reference"
                                        value={attestationData.reference}
                                        onChange={handleAttestationChange}
                                        variant="outlined"
                                        size="small"
                                        disabled={generatingAttestation}
                                        placeholder="Laissez vide pour génération automatique"
                                    />
                                </Grid>
                            </Grid>
                        ) : (
                            <Box position="relative" className={classes.attestationPreview}>
                                {attestationHTML}
                                {generatingAttestation && (
                                    <div className={classes.loadingOverlay}>
                                        <CircularProgress />
                                    </div>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    
                    <DialogActions>
                        {!showAttestationPreview ? (
                            <>
                                <Button onClick={handleCloseAttestation} color="secondary" disabled={generatingAttestation}>
                                    Annuler
                                </Button>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    onClick={handleSubmitAttestation} 
                                    disabled={!attestationData.nomPrenom.trim() || !attestationData.dateEmbauche || generatingAttestation}
                                >
                                    Aperçu
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button onClick={() => setShowAttestationPreview(false)} color="secondary" disabled={generatingAttestation}>
                                    Modifier
                                </Button>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    onClick={generateAttestationPDF} 
                                    disabled={generatingAttestation}
                                >
                                    {generatingAttestation ? <CircularProgress size={24} /> : '📄 Générer PDF'}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            </Container>
        </MainContainer>
    );
}